<template>
    <div class="item input mediaMultiple">
        <div class="container-text">
          
            <div class="author">
                <div class="author-picture">
                    <img :src="currentUser.avatar || 'img/avatar.jpg'"/>
                </div>
                <div class="author-tect-info">
                    <p class="name">{{ $t('dash.posts.drawer.you') }}</p>
                    <p class="date">{{ $t('dash.posts.drawer.just-now') }}</p>
                </div>
            </div>
            <textarea
                v-model="commentValue"
                ref="textarea"
                :class="scrollbarClass"
                rows="1"
                @focus="resize"
                @keyup="resize"
                :placeholder="$t('dash.posts.drawer.write-note')"
            ></textarea>
            
        </div>

        <!-- Preview vocal -->
        <VocalPlayer
            v-if="vocalFile"
            :src="vocalFile.url"
            :transcript="transcript"
            :deletable="true"
            @delete="removeVocal"
        />

        <!-- Preview médias -->
        <div class="container-media" v-if="filesUpload.length">
            <template v-for="(file, index) in filesUpload">
                <div class="image" v-if="file.type.startsWith('image')" @click="$delete(filesUpload, index)">
                    <img :src="file.url"/>
                </div>
                <div class="video" v-else-if="file.type.startsWith('video')" @click="$delete(filesUpload, index)">
                    <video :src="file.url" :type="file.type" muted loop playsinline preload="metadata" class="source"></video>
                </div>
            </template>
        </div>

        <div class="footer">
            <div class="actions">

                <div class="container-add-media">
                    <!-- Pièce jointe -->
                    <label for="fileComment">
                        <i class="fa-regular fa-paperclip"></i>
                    </label>
                    <input
                        v-validate="''"
                        name="fileComment"
                        type="file"
                        id="fileComment"
                        ref="inputMediaComment"
                        @change="selectFiles"
                        :multiple="true"
                        @click="()=>{}"
                        :accept="'.jpg, .jpeg, .png, .pdf, .mp4, .webm, .ogg, .avi, .wmv, .flv, .mkv'"
                        hidden
                    >                
                </div>

                <!-- 1 clic = start, 1 clic = stop -->
                <div
                    class="vocal-btn"
                    @click="toggleRecording"
                    :class="{ recording: isRecording }"
                >
                    <i :class="isRecording ? 'fa-solid fa-stop' : 'fa-regular fa-microphone'"></i>
                </div>

            </div>
            <div class="send" :class="{loading: loading, active: commentValue.length || filesUpload.length || vocalFile}" @click="submit">
                <p>{{ $t('dash.post-creator.modal.send') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import VocalPlayer from './VocalPlayer.vue'
import accessibilityMixin from '@/components/mixins/Accessibility.vue'

export default {
    components: { VocalPlayer },

    mixins: [accessibilityMixin],

    props: {
        currentUser: { type: Object, required: true },
        loading: { type: Boolean, default: false },
    },

    data() {
        return {
            commentValue: '',
            filesUpload: [],

            // Vocal
            isRecording: false,
            mediaRecorder: null,
            audioChunks: [],
            vocalFile: null,
            transcript: '',
            recognition: null,
            showTranscript: false,

        }
    },

    methods: {
        resize() {
            const { textarea } = this.$refs;
            textarea.style.height = 0;
            textarea.style.height = textarea.scrollHeight + 'px';
        },

        selectFiles(e) {
            const files = Array.from(e.target.files);
            this.filesUpload = files.map(file => ({
                url: URL.createObjectURL(file),
                type: file.type,
                file,
            }));
        },

        // ─── Vocal ───────────────────────────────────────────

        toggleRecording() {
            if (this.isRecording) this.stopRecording();
            else this.startRecording();
        },

        async startRecording() {
            if (this.isRecording) return;

            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.audioChunks = [];
                this.transcript = '';
                this.vocalFile = null;

                const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
                this.mediaRecorder = new MediaRecorder(stream, { mimeType });
                this.mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) this.audioChunks.push(e.data);
                };
                this.mediaRecorder.onstop = () => this.buildVocalFile(stream);
                this.mediaRecorder.start();
                this.isRecording = true;

                this.startTranscription();

            } catch (err) {
                console.error('[Vocal] Microphone non accessible :', err);
            }
        },

        stopRecording() {
            if (!this.isRecording || !this.mediaRecorder) return;
            this.mediaRecorder.stop();
            this.isRecording = false;

            if (this.recognition) this.recognition.stop();
        },

        buildVocalFile(stream) {
            stream.getTracks().forEach(t => t.stop());

            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
            const ext = mimeType === 'audio/webm' ? 'webm' : 'mp4';
            const blob = new Blob(this.audioChunks, { type: mimeType });
            const file = new File([blob], `vocal-${Date.now()}.${ext}`, { type: mimeType });
            this.vocalFile = {
                url: URL.createObjectURL(blob),
                file,
                type: mimeType,
            };
        },

        startTranscription() {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                console.warn('[Vocal] Web Speech API non supportée par ce navigateur');
                return;
            }

            this.recognition = new SpeechRecognition();
            this.recognition.lang = 'fr-FR';
            this.recognition.continuous = true;
            this.recognition.interimResults = true;

            this.recognition.onresult = (event) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript) {
                    this.transcript += finalTranscript + ' ';
                    // this.commentValue += finalTranscript + ' ';
                    this.$nextTick(() => this.resize());
                }
            };

            this.recognition.onerror = (e) => {
                console.error('[Vocal] Erreur reconnaissance :', e.error);
            };

            this.recognition.start();
        },

        removeVocal() {
            this.vocalFile = null;
            this.transcript = '';
            this.isPlaying = false;
            this.progress = 0;
            this.formattedTime = '0:00';
            this.showTranscript = false;
        },

        // ─── Submit ───────────────────────────────────────────

        submit() {

            const files = [...this.filesUpload];

            if (this.vocalFile) {
                files.push(this.vocalFile);
        
                if (this.transcript) {
                    const blob = new Blob([this.transcript], { type: 'text/plain' });
                    files.push({
                        url: URL.createObjectURL(blob),
                        type: 'text/plain',
                        file: new File([blob], `transcript-${Date.now()}.txt`, { type: 'text/plain' }),
                    });
                }
            }
            this.$emit('submit', {
                message: this.commentValue,
                files
            });
        },

        reset() {
            this.commentValue = '';
            this.filesUpload = [];
            this.vocalFile = null;
            this.transcript = '';
        },
    },
}
</script>